import type { JobSalary, MetroArea, Quantile, WatchlistDto } from "@lossdog/job-watchlists";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AuthContext } from "@/data/auth/auth.context";
import { useJobSalaryContext } from "@/data/watchlist/JobSalary.context";
import { useUserExperience } from "@/hooks/useUserExperience";
import { DemoContext } from "@/providers/demo/DemoContext";
import { useWatchlistSelectionsStore } from "@/providers/watchlist/watchlist-selections.store";

const METRO_AREA_DEFAULT = "16980";
const QUANTILE_DEFAULT = "a_median";

interface UseJobSalariesOptions {
  defaultToAll?: boolean;
  autoSelectDefaults?: boolean;
}

interface UseJobSalariesReturn {
  // Data
  jobSalaries: JobSalary[] | undefined;
  allJobSalaries: JobSalary[] | undefined;

  // Available options
  watchlistGroups: any[] | undefined;
  watchlists: WatchlistDto[];
  metroAreas: MetroArea[] | undefined;
  quantiles: Quantile[] | undefined;

  // Selected filters
  selectedWatchlistGroup: { id: string; title: string } | null;
  selectedWatchlist: { id: string; title: string } | null;
  selectedMetroArea: MetroArea | null;
  selectedQuantile: Quantile | null;
  selectedJobSalary: JobSalary | null;
  // Actions
  setSelectedWatchlistGroup: (group: { id: string; title: string } | null) => void;
  setSelectedWatchlist: (watchlist: { id: string; title: string } | null) => void;
  setSelectedMetroArea: (metroArea: MetroArea | null) => void;
  setSelectedQuantile: (quantile: Quantile | null) => void;
  setSelectedJobSalary: (jobSalary: JobSalary | null) => void;
  // Loading state
  isLoading: boolean;
}

export const useJobSalaries = (options: UseJobSalariesOptions = {}): UseJobSalariesReturn => {
  const { defaultToAll = false, autoSelectDefaults = true } = options;

  const { jobSalaryClient, isLoading } = useJobSalaryContext();
  const { user } = AuthContext.useAuth();
  const { yearsOfExperience } = useUserExperience();
  const { isDemoMode } = DemoContext.useDemo();
  // Get selections from Zustand store
  const selectedWatchlistGroup = useWatchlistSelectionsStore((state) => state.selectedWatchlistGroup);
  const selectedWatchlist = useWatchlistSelectionsStore((state) => state.selectedWatchlist);
  const selectedMetroArea = useWatchlistSelectionsStore((state) => state.selectedMetroArea);
  const selectedQuantile = useWatchlistSelectionsStore((state) => state.selectedQuantile);
  const selectedJobSalary = useWatchlistSelectionsStore((state) => state.selectedJobSalary);

  const setSelectedWatchlistGroup = useWatchlistSelectionsStore((state) => state.setSelectedWatchlistGroup);
  const setSelectedWatchlist = useWatchlistSelectionsStore((state) => state.setSelectedWatchlist);
  const setSelectedMetroArea = useWatchlistSelectionsStore((state) => state.setSelectedMetroArea);
  const setSelectedQuantile = useWatchlistSelectionsStore((state) => state.setSelectedQuantile);
  const setSelectedJobSalary = useWatchlistSelectionsStore((state) => state.setSelectedJobSalary);

  // Local state for computed data
  const [watchlists, setWatchlists] = useState<WatchlistDto[]>([]);
  const [jobSalaries, setJobSalaries] = useState<JobSalary[] | undefined>(undefined);
  const [allJobSalaries, setAllJobSalaries] = useState<JobSalary[] | undefined>(undefined);

  // Memoized computed values from client
  const watchlistGroups = useMemo(() => jobSalaryClient?.getWatchlistGroups(), [jobSalaryClient]);
  const metroAreas = useMemo(() => jobSalaryClient?.getMetroAreas(), [jobSalaryClient]);
  const quantiles = useMemo(() => jobSalaryClient?.getQuantiles(), [jobSalaryClient]);

  // Find the "All" watchlist for autocomplete
  const allWatchlist = useMemo(() => {
    const allGroup = watchlistGroups?.find((group) => group.title === "All");
    return allGroup?.watchlists?.[0] || null;
  }, [watchlistGroups]);

  // Compute default watchlist group and watchlist IDs
  const { defaultWatchlistGroupId, defaultWatchlistId } = useMemo(() => {
    if (!watchlistGroups?.length) {
      return { defaultWatchlistGroupId: undefined, defaultWatchlistId: undefined };
    }

    // Find "LossDog" group by title
    const lossDogGroup = watchlistGroups.find((group) => group.title.includes("LossDog"));
    if (!lossDogGroup) {
      return { defaultWatchlistGroupId: undefined, defaultWatchlistId: undefined };
    }

    // Find "Tom's Picks" watchlist within the LossDog group
    const tomsPicks = lossDogGroup.watchlists?.find((wl: WatchlistDto) => wl.title.includes("Tom's Picks"));

    return {
      defaultWatchlistGroupId: lossDogGroup.id,
      defaultWatchlistId: tomsPicks?.id,
    };
  }, [watchlistGroups]);

  // Compute default metro area ID from user location
  const computedDefaultMetroAreaId = useMemo(() => {
    const city = user?.dataV02?.person?.profile?.location?.city;
    const state = user?.dataV02?.person?.profile?.location?.state;
    if (!city || !state) {
      return METRO_AREA_DEFAULT; // Chicago fallback
    }
    const cityRegex = new RegExp(`^${city.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i");
    const metroArea = metroAreas?.find((metroArea) => cityRegex.test(metroArea.title));
    return metroArea?.id ?? "16980";
  }, [user, metroAreas]);

  // Compute default quantile ID from user experience
  const computedDefaultQuantileId = useMemo(() => {
    if (typeof yearsOfExperience !== "number" || isDemoMode) {
      return QUANTILE_DEFAULT;
    }

    // Map years of experience to quantile IDs
    if (yearsOfExperience < 2) {
      return "a_pct10"; // 0-1 years
    } else if (yearsOfExperience < 5) {
      return "a_pct25"; // 1-5 years
    } else if (yearsOfExperience < 10) {
      return "a_median"; // 5-10 years
    } else if (yearsOfExperience < 15) {
      return "a_pct75"; // 10-15 years
    } else {
      return "a_pct90";
    }
  }, [yearsOfExperience, isDemoMode]);

  // Fetch job salaries when selections change
  const fetchJobSalaries = useCallback(() => {
    if (isLoading || !jobSalaryClient || !selectedWatchlist || !selectedMetroArea || !selectedQuantile) {
      setJobSalaries(undefined);
      return;
    }

    try {
      const result = jobSalaryClient.getJobSalaries(selectedWatchlist.id, selectedMetroArea.id, selectedQuantile.id);
      setJobSalaries(result?.jobSalaries);
    } catch {
      setJobSalaries(undefined);
    }
  }, [jobSalaryClient, isLoading, selectedWatchlist, selectedMetroArea, selectedQuantile]);

  // Fetch all job salaries for autocomplete
  const fetchAllJobSalaries = useCallback(() => {
    if (isLoading || !jobSalaryClient || !allWatchlist || !selectedMetroArea || !selectedQuantile) {
      setAllJobSalaries(undefined);
      return;
    }

    try {
      const result = jobSalaryClient.getJobSalaries(allWatchlist.id, selectedMetroArea.id, selectedQuantile.id);
      setAllJobSalaries(result?.jobSalaries);
    } catch {
      setAllJobSalaries(undefined);
    }
  }, [jobSalaryClient, isLoading, allWatchlist, selectedMetroArea, selectedQuantile]);

  // Set default selections when data is available
  useEffect(() => {
    if (isLoading || !autoSelectDefaults) return;

    if (watchlistGroups?.length && !selectedWatchlistGroup) {
      // Try to find and set default watchlist group and watchlist by IDs
      if (defaultWatchlistGroupId && defaultWatchlistId) {
        const targetGroup = watchlistGroups.find((group) => group.id === defaultWatchlistGroupId);
        if (targetGroup) {
          const targetWatchlist = targetGroup.watchlists?.find((wl: WatchlistDto) => wl.id === defaultWatchlistId);
          if (targetWatchlist) {
            setSelectedWatchlistGroup(targetGroup);
            setSelectedWatchlist(targetWatchlist);
          }
        } else {
          setSelectedWatchlistGroup(watchlistGroups[0]);
        }
      } else {
        setSelectedWatchlistGroup(watchlistGroups[0]);
      }
    }

    if (selectedWatchlistGroup) {
      const nextWatchlists = watchlistGroups?.find((group) => group.id === selectedWatchlistGroup.id)?.watchlists ?? [];
      setWatchlists(nextWatchlists);

      const isCurrentWatchlistInNewGroup =
        selectedWatchlist && nextWatchlists.some((wl) => wl.id === selectedWatchlist.id);

      if (nextWatchlists.length > 0 && (!selectedWatchlist || !isCurrentWatchlistInNewGroup)) {
        setSelectedWatchlist(nextWatchlists[0]);
      }
    }

    if (metroAreas?.length && !selectedMetroArea) {
      setSelectedMetroArea(
        metroAreas?.find((metroArea) => metroArea.id === computedDefaultMetroAreaId) ?? metroAreas?.[0],
      );
    }

    if (quantiles?.length && !selectedQuantile) {
      setSelectedQuantile(quantiles?.find((quantile) => quantile.id === computedDefaultQuantileId) ?? quantiles?.[0]);
    }
  }, [
    watchlistGroups,
    metroAreas,
    quantiles,
    selectedWatchlistGroup,
    selectedMetroArea,
    selectedQuantile,
    isLoading,
    autoSelectDefaults,
    defaultToAll,
    selectedWatchlist,
    setSelectedWatchlistGroup,
    setSelectedWatchlist,
    setSelectedMetroArea,
    setSelectedQuantile,
    defaultWatchlistGroupId,
    defaultWatchlistId,
    computedDefaultMetroAreaId,
    computedDefaultQuantileId,
  ]);

  // Fetch job salaries when selections change
  useEffect(() => {
    fetchJobSalaries();
  }, [fetchJobSalaries]);

  // Fetch all job salaries when selections change
  useEffect(() => {
    fetchAllJobSalaries();
  }, [fetchAllJobSalaries]);

  useEffect(() => {
    if (!jobSalaryClient || !selectedJobSalary || !selectedMetroArea || !selectedQuantile) {
      return;
    }

    const nextSalary = jobSalaryClient.getOrCreateJobSalary(
      selectedJobSalary.streamName,
      selectedMetroArea,
      selectedQuantile,
    );
    if (nextSalary === selectedJobSalary) {
      return;
    }
    nextSalary.subscribe();
    setSelectedJobSalary(nextSalary);
  }, [jobSalaryClient, selectedJobSalary, setSelectedJobSalary, selectedMetroArea, selectedQuantile]);

  return {
    // Data
    jobSalaries,
    allJobSalaries,

    // Available options
    watchlistGroups,
    watchlists,
    metroAreas,
    quantiles,

    // Selected filters
    selectedWatchlistGroup,
    selectedWatchlist,
    selectedMetroArea,
    selectedQuantile,
    selectedJobSalary,
    // Actions
    setSelectedWatchlistGroup,
    setSelectedWatchlist,
    setSelectedMetroArea,
    setSelectedQuantile,
    setSelectedJobSalary: (jobSalary: JobSalary | null) => {
      // HACK: make sure newly selected job is subscribed
      jobSalary?.subscribe();
      setSelectedJobSalary(jobSalary);
    },

    // Loading state
    isLoading,
  };
};
